/**
 * @zendir/ui - NumberInput Component
 *
 * Numeric input with optional slider, min/max bounds, step, precision,
 * and unit display. Essential for operator dashboards (frequency, power,
 * aperture, field of view, etc.).
 *
 * Astro UX Compliance:
 * - Consistent sizing with Input component (small/medium/large)
 * - Focus ring for accessibility (WCAG 2.1 AA)
 * - Status-based border colors
 * - Tabular numbers for value alignment
 *
 * @example
 * ```tsx
 * // Each instance can set precision (decimal places); default is 2
 * <NumberInput value={500} onChange={setFreq} min={300} max={900} unit="MHz" label="Frequency" />
 * <NumberInput value={45.2} onChange={setPower} min={0.1} max={100} step={0.1} precision={1} unit="W" slider />
 * <NumberInput value={1024} onChange={setRes} min={128} max={2048} step={128} precision={0} unit="px" label="Resolution" slider />
 * // Status mode with automatic threshold-based coloring:
 * <NumberInput value={temp} onChange={setTemp} min={0} max={100} unit="°C" slider
 *   statusMode statusThresholds={{ critical: 90, serious: 75, caution: 50, normal: 20, standby: 0 }} />
 * ```
 */

import React, {
  memo,
  forwardRef,
  useState,
  useCallback,
  useRef,
  useEffect,
  useId,
  useMemo,
} from "react";
import { useTheme } from "../../theme";
import type { LabelPlacement } from "./Input";
import type { StatusLevel } from "../../utils";
import type { StatusThresholds } from "../data/propertyConfig";
import { deriveStatus } from "../data/propertyConfig";
import { StatusIndicator } from "../../astro/StatusIndicator";

export type NumberInputSize = "small" | "medium" | "large";

/**
 * Re-export SliderStatus as an alias for StatusLevel for convenience.
 * Includes: 'normal' | 'standby' | 'caution' | 'serious' | 'critical' | 'off'
 */
export type SliderStatus = StatusLevel;

/** Default decimal places when precision is not specified: 0 = integer display. Set precision (e.g. 2) to show decimals. */
const DEFAULT_DECIMAL_PLACES = 0;

export interface NumberInputProps {
  /** Current value */
  value: number;
  /** Change handler */
  onChange: (value: number) => void;
  /** Minimum value */
  min?: number;
  /** Maximum value */
  max?: number;
  /** Step increment */
  step?: number;
  /** Number of decimal places to show and round to. Omit or 0 = integer. Use 2 (or any positive number) to show decimals; 2 is typical when decimals are needed. */
  precision?: number;
  /** Unit label displayed after the input (e.g., "MHz", "W", "°C") */
  unit?: string;
  /** Label text or ReactNode (supports rich content like tooltip icons) */
  label?: React.ReactNode;
  /** Helper text below the input */
  helperText?: string;
  /** Error state / message */
  error?: boolean | string;
  /** Show slider below the input */
  slider?: boolean;
  /** Size variant */
  size?: NumberInputSize;
  /** Disabled state */
  disabled?: boolean;
  /** Full width */
  fullWidth?: boolean;
  /** Custom width */
  width?: string | number;
  /** ID for accessibility */
  id?: string;
  /** Custom className */
  className?: string;
  /** Custom style */
  style?: React.CSSProperties;
  /** Text alignment inside the input field */
  textAlign?: "left" | "center" | "right";
  /**
   * Label placement style.
   * - `'outlined'` — label sits on the input border (notched/Material-style). **Default.**
   * - `'above'`    — label sits above the input with a gap (classic stack).
   */
  labelPlacement?: LabelPlacement;
  /** Required indicator */
  required?: boolean;

  // ── Status Mode ──────────────────────────────────────────────────────
  /**
   * Enable status-based coloring on the slider track, thumb, and glow.
   * When enabled, the slider color reflects the current status instead of
   * the default accent color.
   */
  statusMode?: boolean;
  /**
   * Manual status override. When provided, this status is used directly
   * instead of computing from thresholds.
   */
  status?: SliderStatus;
  /**
   * Threshold map for automatic status resolution based on current value.
   * Supports both high thresholds (critical, serious, caution) and low
   * thresholds (criticalLow, seriousLow, cautionLow) from the shared
   * Astro UX StatusThresholds type.
   * Requires `statusMode` to be enabled.
   */
  statusThresholds?: StatusThresholds;
  /**
   * Callback fired whenever the computed/resolved status changes.
   * Useful for parent components to react to status transitions.
   */
  onStatusChange?: (status: SliderStatus) => void;
}

// Status resolution is delegated to the shared deriveStatus() utility from propertyConfig.

export const NumberInput = memo(
  forwardRef<HTMLInputElement, NumberInputProps>(function NumberInput(
    {
      value,
      onChange,
      min,
      max,
      step = 1,
      precision,
      unit,
      label,
      helperText,
      error,
      slider = false,
      size = "medium",
      disabled = false,
      fullWidth = false,
      width,
      id,
      className,
      style,
      textAlign = "right",
      labelPlacement = "outlined",
      required,
      statusMode = false,
      status: statusOverride,
      statusThresholds,
      onStatusChange,
    },
    ref,
  ): React.ReactElement {
    const { tokens, theme } = useTheme();
    const isTransparentTheme =
      theme === "transparent" ||
      theme === "transparent-bold" ||
      theme === "transparent-minimal";
    const isOutlined = labelPlacement === "outlined";
    const decimalPlaces = precision ?? DEFAULT_DECIMAL_PLACES;
    const [isFocused, setIsFocused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isSliderActive, setIsSliderActive] = useState(false);
    const [localValue, setLocalValue] = useState(() =>
      value.toFixed(decimalPlaces),
    );
    const inputRef = useRef<HTMLInputElement>(null);
    const sliderContainerRef = useRef<HTMLDivElement>(null);
    const sliderId = useId().replace(/:/g, "");

    // ── Status resolution ──────────────────────────────────────────────────
    const resolvedStatus: SliderStatus | undefined = useMemo(() => {
      if (!statusMode) return undefined;
      if (statusOverride) return statusOverride;
      return deriveStatus(value, statusThresholds);
    }, [statusMode, statusOverride, statusThresholds, value]);

    // Fire onStatusChange when status changes
    const prevStatusRef = useRef<SliderStatus | undefined>(undefined);
    useEffect(() => {
      if (
        resolvedStatus !== undefined &&
        resolvedStatus !== prevStatusRef.current
      ) {
        prevStatusRef.current = resolvedStatus;
        onStatusChange?.(resolvedStatus);
      }
    }, [resolvedStatus, onStatusChange]);

    /** Resolve the active color: status color or accent */
    const activeColor = useMemo(() => {
      if (resolvedStatus && tokens.colors.status[resolvedStatus]) {
        return tokens.colors.status[resolvedStatus];
      }
      return tokens.colors.accent.primary;
    }, [resolvedStatus, tokens.colors.status, tokens.colors.accent.primary]);

    // Sync external value to local state
    useEffect(() => {
      if (!isFocused) {
        setLocalValue(value.toFixed(decimalPlaces));
      }
    }, [value, decimalPlaces, isFocused]);

    const clamp = useCallback(
      (v: number): number => {
        let result = v;
        if (min !== undefined) result = Math.max(min, result);
        if (max !== undefined) result = Math.min(max, result);
        result = Number(result.toFixed(decimalPlaces));
        return result;
      },
      [min, max, decimalPlaces],
    );

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        setLocalValue(raw);

        const parsed = parseFloat(raw);
        if (!isNaN(parsed)) {
          onChange(clamp(parsed));
        }
      },
      [onChange, clamp],
    );

    const handleBlur = useCallback(() => {
      setIsFocused(false);
      const parsed = parseFloat(localValue);
      if (isNaN(parsed)) {
        setLocalValue(value.toFixed(decimalPlaces));
      } else {
        const clamped = clamp(parsed);
        onChange(clamped);
        setLocalValue(clamped.toFixed(decimalPlaces));
      }
    }, [localValue, value, onChange, clamp, decimalPlaces]);

    const handleSliderChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const parsed = parseFloat(e.target.value);
        const clamped = clamp(parsed);
        onChange(clamped);
        setLocalValue(clamped.toFixed(decimalPlaces));
      },
      [onChange, clamp, decimalPlaces],
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "ArrowUp") {
          e.preventDefault();
          onChange(clamp(value + step));
        } else if (e.key === "ArrowDown") {
          e.preventDefault();
          onChange(clamp(value - step));
        }
      },
      [value, step, onChange, clamp],
    );

    // Wheel handler: scroll on slider or input changes value
    const handleWheel = useCallback(
      (e: WheelEvent) => {
        if (disabled || min === undefined || max === undefined) return;
        e.preventDefault();
        const direction = e.deltaY < 0 ? 1 : -1;
        onChange(clamp(value + step * direction));
      },
      [value, step, onChange, clamp, disabled, min, max],
    );

    // Attach wheel listener to slider container AND input element
    useEffect(() => {
      const sliderContainer = sliderContainerRef.current;
      const inputElement = inputRef.current;

      // Always attach to input if min/max defined, regardless of slider prop
      if (min !== undefined && max !== undefined) {
        if (slider && sliderContainer) {
          sliderContainer.addEventListener("wheel", handleWheel, {
            passive: false,
          });
        }
        if (inputElement) {
          inputElement.addEventListener("wheel", handleWheel, {
            passive: false,
          });
        }
      }

      return () => {
        if (sliderContainer)
          sliderContainer.removeEventListener("wheel", handleWheel);
        if (inputElement)
          inputElement.removeEventListener("wheel", handleWheel);
      };
    }, [handleWheel, slider, min, max]);

    // Release slider active state when mouse is released anywhere
    useEffect(() => {
      if (!isSliderActive) return;
      const handleUp = () => setIsSliderActive(false);
      window.addEventListener("mouseup", handleUp);
      window.addEventListener("touchend", handleUp);
      return () => {
        window.removeEventListener("mouseup", handleUp);
        window.removeEventListener("touchend", handleUp);
      };
    }, [isSliderActive]);

    const [isUnitDragging, setIsUnitDragging] = useState(false);
    const dragStartValueRef = useRef<number>(0);
    const dragStartYRef = useRef<number>(0);
    const unitRef = useRef<HTMLSpanElement>(null);

    // Unit Drag Handler
    const handleUnitMouseDown = useCallback(
      (e: React.MouseEvent) => {
        if (disabled) return;

        e.preventDefault();
        setIsUnitDragging(true);
        dragStartValueRef.current = value;
        dragStartYRef.current = e.clientY;

        // Lock cursor if supported for infinite drag
        if (document.body.requestPointerLock) {
          document.body.requestPointerLock();
        }
      },
      [value, disabled],
    );

    const handleUnitMouseMove = useCallback(
      (e: MouseEvent) => {
        if (!isUnitDragging) return;

        // Use movementY if pointer is locked (infinite drag), otherwise fallback to delta
        // Invert Y because dragging UP (negative Y) should INCREASE value
        const deltaY = document.pointerLockElement
          ? -e.movementY
          : dragStartYRef.current - e.clientY;

        if (deltaY === 0) return;

        // Velocity / Precision Logic
        // Shift key = Precision mode (0.1x speed)
        // Ctrl/Cmd key = Fast mode (10x speed)
        let speedMultiplier = 1;
        if (e.shiftKey) speedMultiplier = 0.1;
        else if (e.ctrlKey || e.metaKey) speedMultiplier = 10;

        // Scale sensitivity: 1 pixel = 1 step * multiplier
        // For non-locked pointer, we might want to dampen it slightly to 0.5 step per pixel
        const sensitivity = document.pointerLockElement ? 1 : 0.5;

        const change = deltaY * sensitivity * (step || 1) * speedMultiplier;

        // If pointer is NOT locked, we update reference to prevent jumpiness
        if (!document.pointerLockElement) {
          dragStartYRef.current = e.clientY;
          dragStartValueRef.current = clamp(value + change);
          onChange(clamp(value + change));
        } else {
          // For locked pointer, we accumulate on the start value or current value
          // Better to accumulate on current value to handle continuous flow
          onChange(clamp(value + change));
        }
      },
      [isUnitDragging, value, step, onChange, clamp],
    );

    const handleUnitMouseUp = useCallback(() => {
      if (isUnitDragging) {
        setIsUnitDragging(false);
        if (document.exitPointerLock) {
          document.exitPointerLock();
        }
      }
    }, [isUnitDragging]);

    // Global mouse listeners for drag
    useEffect(() => {
      if (isUnitDragging) {
        window.addEventListener("mousemove", handleUnitMouseMove);
        window.addEventListener("mouseup", handleUnitMouseUp);
      }
      return () => {
        window.removeEventListener("mousemove", handleUnitMouseMove);
        window.removeEventListener("mouseup", handleUnitMouseUp);
      };
    }, [isUnitDragging, handleUnitMouseMove, handleUnitMouseUp]);

    // Attach wheel listener to unit element too
    useEffect(() => {
      const unitEl = unitRef.current;
      if (unitEl && !disabled) {
        unitEl.addEventListener("wheel", handleWheel, { passive: false });
      }
      return () => {
        if (unitEl) unitEl.removeEventListener("wheel", handleWheel);
      };
    }, [handleWheel, disabled]);

    // Size config — heights match Input & Select via elementSize tokens
    const sizeConfig = {
      small: {
        height: tokens.elementSize.sm,
        fontSize: tokens.typography.fontSize.xs,
        padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
        sliderHeight: 3,
        inputPaddingLeft: 8,
      },
      medium: {
        height: tokens.elementSize.md,
        fontSize: tokens.typography.fontSize.sm,
        padding: `${tokens.spacing.xs} ${tokens.spacing.smd}`,
        sliderHeight: 4,
        inputPaddingLeft: 12,
      },
      large: {
        height: tokens.elementSize.lg,
        fontSize: tokens.typography.fontSize.base,
        padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
        sliderHeight: 5,
        inputPaddingLeft: 16,
      },
    };
    const config = sizeConfig[size];

    const hasError = !!error;
    const errorMessage = typeof error === "string" ? error : undefined;

    const computedBorder = hasError
      ? tokens.borders.input.error
      : isFocused
        ? tokens.borders.input.focus
        : isHovered
          ? tokens.borders.input.hover
          : tokens.borders.input.default;

    const inputBg = isTransparentTheme
      ? tokens.colors.interactive.transparentInputBg ||
        "rgba(139, 92, 246, 0.08)"
      : tokens.colors.background.surface;

    // Slider track gradient (filled portion)
    const sliderPercent =
      min !== undefined && max !== undefined
        ? ((value - min) / (max - min)) * 100
        : 50;

    const inputId =
      id ||
      (typeof label === "string"
        ? `zendir-number-${label.replace(/\s+/g, "-").toLowerCase()}`
        : undefined);

    // ── Thumb dimensions ─────────────────────────────────────────────────
    const thumbWidth = 24;
    const thumbHeight = config.sliderHeight + 8;
    const trackH = config.sliderHeight;

    return (
      <div
        className={className}
        style={{
          display: "inline-flex",
          flexDirection: "column",
          // No gap when slider is present — slider sits flush on the input bottom edge
          gap: slider ? 0 : tokens.spacing.xs,
          width: fullWidth
            ? "100%"
            : typeof width === "number"
              ? `${width}px`
              : width,
          fontFamily: tokens.typography.fontFamily.primary,
          ...style,
        }}
      >
        {/* Label: 'above' variant */}
        {label && !isOutlined && (
          <label
            htmlFor={inputId}
            style={{
              fontSize: tokens.typography.fontSize.xs,
              fontWeight: tokens.typography.fontWeight.medium,
              color: tokens.colors.text.secondary,
              letterSpacing: tokens.typography.letterSpacing.wide,
            }}
          >
            {label}
            {required && (
              <span
                style={{
                  color: tokens.colors.status.critical,
                  marginLeft: "2px",
                }}
              >
                *
              </span>
            )}
          </label>
        )}

        {/* Input row */}
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            height: config.height,
            backgroundColor: inputBg,
            border: computedBorder,
            borderRadius: tokens.borderRadius.md,
            transition: tokens.animation.fast,
            opacity: disabled ? 0.5 : 1,
            overflow: isOutlined ? "visible" : "hidden",
          }}
          onMouseEnter={() => !disabled && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Label: 'outlined' variant — positioned on the input row border */}
          {label && isOutlined && (
            <label
              htmlFor={inputId}
              style={{
                position: "absolute",
                top: 0,
                left: `${config.inputPaddingLeft - 6}px`,
                transform: "translateY(-50%)",
                backgroundColor: isTransparentTheme
                  ? tokens.colors.background.base
                  : tokens.colors.background.surface,
                padding: "0 6px",
                fontSize: {
                  small: "0.625rem",
                  medium: "0.6875rem",
                  large: "0.75rem",
                }[size],
                fontWeight: tokens.typography.fontWeight.medium,
                color: isFocused
                  ? hasError
                    ? tokens.colors.status.critical
                    : tokens.colors.accent.primary
                  : hasError
                    ? tokens.colors.status.critical
                    : tokens.colors.text.secondary,
                letterSpacing: tokens.typography.letterSpacing.wide,
                zIndex: 1,
                pointerEvents: "none",
                transition: tokens.animation.fast,
                lineHeight: 1.2,
                whiteSpace: "nowrap",
              }}
            >
              {label}
              {required && (
                <span
                  style={{
                    color: tokens.colors.status.critical,
                    marginLeft: "2px",
                  }}
                >
                  *
                </span>
              )}
            </label>
          )}
          <input
            ref={ref || inputRef}
            id={inputId}
            type="text"
            inputMode="decimal"
            value={localValue}
            onChange={handleInputChange}
            onFocus={() => setIsFocused(true)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            aria-invalid={hasError}
            aria-describedby={
              errorMessage
                ? `${inputId}-error`
                : helperText
                  ? `${inputId}-helper`
                  : undefined
            }
            style={{
              flex: 1,
              height: "100%",
              padding: config.padding,
              fontSize: config.fontSize,
              fontWeight: tokens.typography.fontWeight.medium,
              fontFamily: tokens.typography.fontFamily.mono,
              fontVariantNumeric: "tabular-nums",
              color: tokens.colors.text.primary,
              backgroundColor: "transparent",
              border: "none",
              outline: "none",
              minWidth: 0,
              textAlign,
            }}
          />

          {/* Unit */}
          {unit && (
            <span
              ref={unitRef}
              onMouseDown={handleUnitMouseDown}
              style={{
                padding: `0 ${tokens.spacing.sm}`,
                fontSize: tokens.typography.fontSize.xs,
                fontWeight: tokens.typography.fontWeight.medium,
                color: isUnitDragging
                  ? activeColor
                  : tokens.colors.text.tertiary,
                fontFamily: tokens.typography.fontFamily.primary,
                flexShrink: 0,
                borderLeft: tokens.borders.separator,
                height: "100%",
                display: "flex",
                alignItems: "center",
                backgroundColor: isTransparentTheme
                  ? `${tokens.colors.accent.primary}0a`
                  : "rgba(0, 0, 0, 0.15)",
                cursor: disabled ? "not-allowed" : "ns-resize",
                userSelect: "none",
                transition: "color 0.2s ease",
              }}
              title={disabled ? undefined : "Drag to adjust value"}
            >
              {unit}
            </span>
          )}
        </div>

        {/* Slider — directly below input with no gap */}
        {slider &&
          min !== undefined &&
          max !== undefined &&
          (() => {
            const sliderH = thumbHeight;
            const cy = sliderH / 2;
            const pct = sliderPercent / 100; // 0..1

            // Thumb left-edge position: maps 0% → 0px, 100% → (containerWidth - thumbWidth)
            // CSS calc: pct * (100% - thumbWidth)
            const thumbLeftCalc = `calc(${pct} * (100% - ${thumbWidth}px))`;

            // Filled track width = thumb left edge + 1px overlap for seamless join
            const filledWidthCalc = `calc(${pct} * (100% - ${thumbWidth}px) + 1px)`;

            return (
              <div
                ref={sliderContainerRef}
                style={{
                  position: "relative",
                  height: `${sliderH}px`,
                  marginTop: `-${sliderH / 1.42}px`,
                }}
              >
                {/* Unfilled track */}
                <div
                  style={{
                    position: "absolute",
                    top: `${cy}px`,
                    left: 0,
                    right: 0,
                    height: `${trackH}px`,
                    transform: "translateY(-50%)",
                    borderRadius: `${trackH / 2}px`,
                    backgroundColor: `${tokens.colors.border.muted}60`,
                  }}
                />
                {/* Filled track — flat right edge for flush join with thumb */}
                <div
                  style={{
                    position: "absolute",
                    top: `${cy}px`,
                    left: 0,
                    width: filledWidthCalc,
                    height: `${trackH}px`,
                    transform: "translateY(-50%)",
                    borderRadius: `${trackH / 2}px 0 0 ${trackH / 2}px`,
                    background: activeColor,
                    transition: "background 300ms ease",
                  }}
                />
                {/* Invisible range input */}
                <input
                  type="range"
                  className={`zendir-slider-${sliderId}`}
                  min={min}
                  max={max}
                  step={step}
                  value={value}
                  onChange={handleSliderChange}
                  onMouseDown={() => setIsSliderActive(true)}
                  onMouseUp={() => setIsSliderActive(false)}
                  onTouchStart={() => setIsSliderActive(true)}
                  onTouchEnd={() => setIsSliderActive(false)}
                  disabled={disabled}
                  aria-label={
                    typeof label === "string" ? label : "Value slider"
                  }
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    margin: 0,
                    padding: 0,
                    opacity: 0,
                    cursor: disabled ? "not-allowed" : "pointer",
                    zIndex: 2,
                  }}
                />
                {/* Smooth-blob thumb — left edge flush with filled track end */}
                <div
                  style={{
                    position: "absolute",
                    top: `${cy}px`,
                    left: thumbLeftCalc,
                    width: `${thumbWidth}px`,
                    height: `${thumbHeight}px`,
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                    zIndex: 1,
                  }}
                >
                  <svg
                    width={thumbWidth}
                    height={thumbHeight}
                    viewBox={`0 0 ${thumbWidth} ${thumbHeight}`}
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ display: "block", overflow: "visible" }}
                  >
                    {(() => {
                      const w = thumbWidth;
                      const h = thumbHeight;
                      const svgCy = h / 2;
                      const trackHalf = trackH / 2;
                      const r = 2;

                      const ltop = svgCy - trackHalf;
                      const lbot = svgCy + trackHalf;
                      const rtop = r;
                      const rbot = h - r;

                      const cp1x = w * 0.28;
                      const cp2x = w * 0.48;

                      const d = [
                        `M 0 ${ltop}`,
                        `C ${cp1x} ${ltop}, ${cp2x} ${rtop}, ${w - r} ${rtop}`,
                        `A ${r} ${r} 0 0 1 ${w} ${rtop + r}`,
                        `L ${w} ${rbot - r}`,
                        `A ${r} ${r} 0 0 1 ${w - r} ${rbot}`,
                        `C ${cp2x} ${rbot}, ${cp1x} ${lbot}, 0 ${lbot}`,
                        `Z`,
                      ].join(" ");

                      return <path d={d} fill={activeColor} />;
                    })()}
                  </svg>
                </div>

                {/* Astro UXDS status symbol (shape-coded: circle/square/diamond/triangle) */}
                {statusMode && resolvedStatus && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: `-${size === "large" ? 10 : 8}px`,
                      left: `calc(${pct} * (100% - ${thumbWidth}px) + ${thumbWidth / 2}px)`,
                      transform: "translateX(-50%)",
                      zIndex: 1,
                      lineHeight: 0,
                    }}
                  >
                    <StatusIndicator status={resolvedStatus} small />
                  </div>
                )}
              </div>
            );
          })()}

        {/* Helper / Error text */}
        {(helperText || errorMessage) && (
          <div
            id={errorMessage ? `${inputId}-error` : `${inputId}-helper`}
            role={errorMessage ? "alert" : undefined}
            style={{
              fontSize: tokens.typography.fontSize.xs,
              color: hasError
                ? tokens.colors.status.critical
                : tokens.colors.text.tertiary,
            }}
          >
            {errorMessage || helperText}
          </div>
        )}
      </div>
    );
  }),
);

export default NumberInput;
